import React from 'react';
import { Play, Pause, Heart, Download, Share2, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Link } from 'react-router-dom';
import DownloadMenu from './DownloadMenu';
import { useToast } from '../hooks/use-toast';

function formatPlays(n) {
  if (n >= 1_000_000) return (n/1_000_000).toFixed(1)+'M';
  if (n >= 1_000) return (n/1_000).toFixed(1)+'K';
  return String(n);
}

export default function SongCard({ song, list, layout = 'grid' }) {
  const { lang, current, isPlaying, playSong, togglePlay, toggleLike, liked, t } = useApp();
  const { toast } = useToast();
  const isCurrent = current?.id === song.id;
  const playing = isCurrent && isPlaying;
  const isLiked = liked.includes(song.id);

  const onPlay = (e) => {
    e.stopPropagation();
    if (isCurrent) togglePlay();
    else playSong(song, list);
  };

  const copyShare = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/s/${song.id}`);
      toast({ title: t('share.copied') });
    } catch {}
  };

  if (layout === 'row') {
    return (
      <div className="group flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors">
        <div className="relative w-12 h-12 shrink-0">
          <img src={song.cover} alt="" className="w-12 h-12 rounded-md object-cover" />
          <button onClick={onPlay} className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
            {playing ? <Pause size={18} className="text-white"/> : <Play size={18} className="text-white" />}
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <Link to={`/s/${song.id}`} className="text-sm font-medium truncate block hover:underline">{song.title[lang] || song.title.en}</Link>
          <div className="text-xs text-zinc-500 truncate">
            {song.ownerId ? <Link to={`/u/${song.ownerId}`} className="hover:text-zinc-300">{song.ownerName || song.artist}</Link> : <span>{song.artist}</span>}
            <span> · {song.genre}</span>
          </div>
        </div>
        <div className="hidden sm:block text-xs text-zinc-500">{formatPlays(song.plays)}</div>
        <button onClick={copyShare} title="Share" className="p-2 rounded hover:bg-white/10 text-zinc-400"><Share2 size={16} /></button>
        <DownloadMenu song={song} trigger={
          <button onClick={(e)=>e.stopPropagation()} title="Download" className="p-2 rounded hover:bg-white/10 text-zinc-400"><Download size={16} /></button>
        } />
        <button onClick={(e)=>{e.stopPropagation(); toggleLike(song.id);}} className={`p-2 rounded hover:bg-white/10 ${isLiked?'text-pink-500':'text-zinc-400'}`}>
          <Heart size={16} fill={isLiked ? 'currentColor':'none'} />
        </button>
      </div>
    );
  }

  return (
    <div className="card-lift group bg-[#101012] border border-white/5 rounded-xl overflow-hidden">
      <div className="relative aspect-square">
        <img src={song.cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
        {song.sunoStatus === 'pending' && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/20 text-xs text-white">
              <Loader2 size={14} className="animate-spin text-pink-400" />
              <span>{lang==='fr'?'Génération...':'Generating...'}</span>
            </div>
          </div>
        )}
        {song.sunoStatus === 'failed' && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-red-500/80 text-white text-[10px] uppercase tracking-wider">
            {lang==='fr'?'Échec':'Failed'}
          </div>
        )}
        <button onClick={onPlay} disabled={song.sunoStatus === 'pending'} className="absolute right-3 bottom-3 w-11 h-11 rounded-full bg-white text-black shadow-lg flex items-center justify-center opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-transform disabled:opacity-30 disabled:cursor-not-allowed">
          {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>
        <div className="absolute top-3 left-3 pill bg-black/50 backdrop-blur border-white/10">{song.genre}</div>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/s/${song.id}`} className="text-sm font-semibold truncate block hover:underline">{song.title[lang] || song.title.en}</Link>
            {song.ownerId ? (
              <Link to={`/u/${song.ownerId}`} className="text-xs text-zinc-500 hover:text-zinc-300 truncate">{song.ownerName || song.artist}</Link>
            ) : (
              <span className="text-xs text-zinc-500 truncate">{song.artist}</span>
            )}
          </div>
          <button onClick={(e)=>{e.stopPropagation(); toggleLike(song.id);}} className={`p-1.5 rounded hover:bg-white/10 ${isLiked?'text-pink-500':'text-zinc-400'}`}>
            <Heart size={16} fill={isLiked?'currentColor':'none'} />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
          <span>{formatPlays(song.plays)} plays</span>
          <div className="flex items-center gap-1">
            <button onClick={copyShare} title="Share" className="hover:text-zinc-200 p-1 -m-1 rounded"><Share2 size={14} /></button>
            <DownloadMenu song={song} trigger={
              <button onClick={(e)=>e.stopPropagation()} title="Download" className="hover:text-zinc-200 p-1 -m-1 rounded"><Download size={14} /></button>
            } />
          </div>
        </div>
      </div>
    </div>
  );
}
